<template>
  <div class="blocklist-view pa-4">
    <div class="d-flex align-center mb-6">
      <v-btn icon="mdi-arrow-left" variant="text" @click="$emit('back')" class="mr-2"></v-btn>
      <h1 class="text-h4 font-weight-black">Blocklist</h1>
    </div>

    <v-alert type="info" variant="tonal" class="mb-6 rounded-xl border">
      These accounts are blocked by the administrator 
      <strong class="text-primary">{{ adminHandle }}</strong>. 
      Posts from these accounts are filtered out from all feeds.
      <div class="mt-2 text-caption opacity-80">
        If you find any mistakes or want to suggest accounts to be added, please contact the administrator.
      </div>
    </v-alert>

    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      <p class="mt-4 text-grey">Resolving blocked identities...</p>
    </div>

    <div v-else-if="blockedProfiles.length > 0">
      <v-list bg-color="transparent" class="pa-0">
        <v-list-item
          v-for="profile in blockedProfiles"
          :key="profile.did"
          class="rounded-xl mb-3 border border-white-05 bg-surface-variant-subtle pa-4"
          @click="viewProfile(profile.handle)"
        >
          <template v-slot:prepend>
            <v-avatar size="56" class="mr-4 border border-white-10">
              <v-img v-if="profile.avatar" :src="profile.avatar"></v-img>
              <v-icon v-else icon="mdi-account" size="32" color="grey"></v-icon>
            </v-avatar>
          </template>

          <v-list-item-title class="text-h6 font-weight-black">
            {{ profile.displayName || profile.handle }}
          </v-list-item-title>
          <v-list-item-subtitle class="text-subtitle-1 text-primary font-weight-bold">
            @{{ profile.handle }}
          </v-list-item-subtitle>
          
          <div class="text-caption text-grey mt-1">{{ profile.did }}</div>

          <template v-slot:append>
             <v-btn icon="mdi-chevron-right" variant="text" color="grey"></v-btn>
          </template>
        </v-list-item>
      </v-list>
    </div>

    <div v-else class="text-center py-12 border rounded-xl border-dashed border-white-05">
      <v-icon icon="mdi-shield-check-outline" size="64" color="grey-darken-2" class="mb-4"></v-icon>
      <h3 class="text-h5 text-grey">No accounts are currently blocked</h3>
      <p class="text-body-2 text-grey-darken-1 mt-2">The community is currently blockhouse-free.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { adminBlocks, getOtherProfile, initAdminBlocks } from '@/services/bluesky';
import { useRouter } from 'vue-router';

const emit = defineEmits(['back', 'view-profile']);
const router = useRouter();
const blockedProfiles = ref<any[]>([]);
const loading = ref(true);
const adminHandle = import.meta.env.VITE_BSKY_ADMIN || 'igrir.bsky.social';

const loadBlockedProfiles = async () => {
  loading.value = true;
  await initAdminBlocks(); // Ensure blocks are loaded
  
  const profiles: any[] = [];
  
  // Convert Set to Array
  const dids = Array.from(adminBlocks.value);
  
  for (const did of dids) {
    try {
      const profile = await getOtherProfile(did);
      if (profile) {
        profiles.push(profile);
      } else {
        profiles.push({ did, handle: did, displayName: 'Unknown Account' });
      }
    } catch (e) {
      profiles.push({ did, handle: did, displayName: 'Unknown Account' });
    }
  }
  
  blockedProfiles.value = profiles;
  loading.value = false;
};

const viewProfile = (handle: string) => {
  emit('view-profile', handle);
};

onMounted(() => {
  loadBlockedProfiles();
});
</script>

<style scoped>
.bg-surface-variant-subtle {
  background: rgba(255, 255, 255, 0.02) !important;
}
.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}
.border-white-10 {
  border-color: rgba(255, 255, 255, 0.1) !important;
}
</style>
